//*Libraries imports
import { Controller } from "react-hook-form";
import {
  MapPin,
  Pencil,
  Check,
  User,
  Mail,
  Phone,
  AlertCircle,
} from "lucide-react";

//*Hooks Imports
import { useFormContext } from "react-hook-form";
import { useEffect, useState } from "react";

//* Utils
import { getAddressByCep } from "@/utils/cepUtils";
import { maskCep, maskPhone, maskCpfCnpj } from "@/utils/maskUtils";
import { Loader2 } from "lucide-react";

//* Types
import { CreditCardFormDTO } from "@/schemas/asaas/payment";

interface BillingAddressSectionProps {
  isEditing: boolean;
  setIsEditing: (val: boolean) => void;
}

export function BillingAddressSection({
  isEditing,
  setIsEditing,
}: BillingAddressSectionProps) {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreditCardFormDTO>();

  const postalCode = watch("creditCardHolderInfo.postalCode");

  const [isCepLoading, setIsCepLoading] = useState(false);
  const [isCepValid, setIsCepValid] = useState<boolean | null>(null);

  useEffect(() => {
    const cleanedCep = postalCode?.replace(/\D/g, "");
    if (cleanedCep?.length === 8) {
      setIsCepLoading(true);
      getAddressByCep(cleanedCep)
        .then((data) => {
          setIsCepValid(!!data);
        })
        .finally(() => {
          setIsCepLoading(false);
        });
    } else {
      setIsCepValid(null);
    }
  }, [postalCode]);

  const addressErrors = errors.creditCardHolderInfo;
  const hasAddressErrors =
    addressErrors && Object.keys(addressErrors).length > 0;

  return (
    <div className="bg-[#FCFCFC] rounded-[18px] p-5 border border-[#EDF2F6] flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="size-5 text-claroprimaria-01" />
          <h3 className="font-bold text-clarotexto-201">
            Endereço de Faturamento
          </h3>
        </div>
        <button
          type="button"
          onClick={() => setIsEditing(!isEditing)}
          className="text-claroprimaria-01 text-sm font-semibold flex items-center gap-1.5 hover:opacity-80 transition-opacity bg-claroprimaria-01/10 px-3 py-1.5 rounded-lg"
        >
          {isEditing ? (
            <>
              <Check className="size-4" /> Concluir
            </>
          ) : (
            <>
              <Pencil className="size-4" /> Editar
            </>
          )}
        </button>
      </div>

      {hasAddressErrors && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-3 flex flex-col gap-2 animate-in fade-in zoom-in duration-200">
          <div className="flex items-center gap-2 text-red-600 font-bold text-xs uppercase tracking-wider">
            <AlertCircle className="size-4" />
            <span>Erros no endereço</span>
          </div>
          <ul className="list-disc list-inside">
            {Object.entries(addressErrors).map(([key, error]) => (
              <li key={key} className="text-[11px] text-red-500 font-medium">
                {typeof error === "string"
                  ? error
                  : error && typeof error === "object" && "message" in error
                    ? String(error.message)
                    : String(error)}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isEditing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4 pt-2">
          <div className="flex items-start gap-2.5">
            <User
              className={`size-4 mt-0.5 ${addressErrors?.name ? "text-red-500" : "text-gray-400"}`}
            />
            <div className="flex flex-col">
              <span
                className={`text-[10px] uppercase tracking-wider font-bold ${addressErrors?.name ? "text-red-500" : "text-gray-400"}`}
              >
                Titular
              </span>
              <span className="text-sm text-gray-700 font-medium truncate max-w-[150px]">
                {watch("creditCardHolderInfo.name") || "Não informado"}
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Mail
              className={`size-4 mt-0.5 ${addressErrors?.email ? "text-red-500" : "text-gray-400"}`}
            />
            <div className="flex flex-col">
              <span
                className={`text-[10px] uppercase tracking-wider font-bold ${addressErrors?.email ? "text-red-500" : "text-gray-400"}`}
              >
                E-mail
              </span>
              <span className="text-sm text-gray-700 font-medium truncate max-w-[150px]">
                {watch("creditCardHolderInfo.email") || "Não informado"}
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <MapPin
              className={`size-4 mt-0.5 ${addressErrors?.postalCode || addressErrors?.addressNumber ? "text-red-500" : "text-gray-400"}`}
            />
            <div className="flex flex-col">
              <span
                className={`text-[10px] uppercase tracking-wider font-bold ${addressErrors?.postalCode || addressErrors?.addressNumber ? "text-red-500" : "text-gray-400"}`}
              >
                Endereço
              </span>
              <span className="text-sm text-gray-700 font-medium">
                CEP: {watch("creditCardHolderInfo.postalCode") || "00000-000"},
                Nº{" "}
                <span
                  className={addressErrors?.addressNumber ? "text-red-500" : ""}
                >
                  {watch("creditCardHolderInfo.addressNumber") || "S/N"}
                </span>
              </span>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <Phone
              className={`size-4 mt-0.5 ${addressErrors?.phone ? "text-red-500" : "text-gray-400"}`}
            />
            <div className="flex flex-col">
              <span
                className={`text-[10px] uppercase tracking-wider font-bold ${addressErrors?.phone ? "text-red-500" : "text-gray-400"}`}
              >
                Telefone
              </span>
              <span className="text-sm text-gray-700 font-medium">
                {watch("creditCardHolderInfo.phone") || "Não informado"}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4 pt-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                className={`text-xs font-medium ${addressErrors?.name ? "text-red-500" : "text-gray-500"}`}
              >
                Nome Completo <span className="text-red-500">*</span>
              </label>
              <Controller
                name="creditCardHolderInfo.name"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    placeholder="Nome Completo"
                    className={`border rounded-xl h-11 px-4 text-sm outline-none transition-colors ${addressErrors?.name ? "border-red-300 bg-red-50 focus:border-red-500" : "border-gray-200 focus:border-claroprimaria-01"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                className={`text-xs font-medium ${addressErrors?.email ? "text-red-500" : "text-gray-500"}`}
              >
                E-mail <span className="text-red-500">*</span>
              </label>
              <Controller
                name="creditCardHolderInfo.email"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    type="email"
                    placeholder="exemplo@email.com"
                    className={`border rounded-xl h-11 px-4 text-sm outline-none transition-colors ${addressErrors?.email ? "border-red-300 bg-red-50 focus:border-red-500" : "border-gray-200 focus:border-claroprimaria-01"}`}
                  />
                )}
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex flex-col gap-1.5">
              <label
                className={`text-xs font-medium ${addressErrors?.cpfCnpj ? "text-red-500" : "text-gray-500"}`}
              >
                CPF/CNPJ <span className="text-red-500">*</span>
              </label>
              <Controller
                name="creditCardHolderInfo.cpfCnpj"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    placeholder="000.000.000-00"
                    onChange={(e) =>
                      field.onChange(maskCpfCnpj(e.target.value))
                    }
                    className={`border rounded-xl h-11 px-4 text-sm outline-none transition-colors ${addressErrors?.cpfCnpj ? "border-red-300 bg-red-50 focus:border-red-500" : "border-gray-200 focus:border-claroprimaria-01"}`}
                  />
                )}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                className={`text-xs font-medium ${addressErrors?.postalCode || isCepValid === false ? "text-red-500" : "text-gray-500"}`}
              >
                CEP <span className="text-red-500">*</span>
              </label>
              <Controller
                name="creditCardHolderInfo.postalCode"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <input
                      {...field}
                      placeholder="00000-000"
                      onChange={(e) => field.onChange(maskCep(e.target.value))}
                      className={`w-full border rounded-xl h-11 px-4 pr-10 text-sm outline-none transition-colors ${addressErrors?.postalCode || isCepValid === false ? "border-red-300 bg-red-50 focus:border-red-500" : "border-gray-200 focus:border-claroprimaria-01"}`}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      {isCepLoading && (
                        <Loader2 className="size-4 animate-spin text-claroprimaria-01" />
                      )}
                      {!isCepLoading && isCepValid === true && (
                        <Check className="size-4 text-green-500" />
                      )}
                      {!isCepLoading && isCepValid === false && (
                        <AlertCircle className="size-4 text-red-500" />
                      )}
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label
                className={`text-xs font-medium ${addressErrors?.addressNumber ? "text-red-500" : "text-gray-500"}`}
              >
                Número <span className="text-red-500">*</span>
              </label>
              <Controller
                name="creditCardHolderInfo.addressNumber"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    placeholder="S/N"
                    className={`border rounded-xl h-11 px-4 text-sm outline-none transition-colors ${addressErrors?.addressNumber ? "border-red-300 bg-red-50 focus:border-red-500" : "border-gray-200 focus:border-claroprimaria-01"}`}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              className={`text-xs font-medium ${addressErrors?.phone ? "text-red-500" : "text-gray-500"}`}
            >
              Telefone <span className="text-red-500">*</span>
            </label>
            <Controller
              name="creditCardHolderInfo.phone"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder="(00) 00000-0000"
                  onChange={(e) => field.onChange(maskPhone(e.target.value))}
                  className={`border rounded-xl h-11 px-4 text-sm outline-none transition-colors ${addressErrors?.phone ? "border-red-300 bg-red-50 focus:border-red-500" : "border-gray-200 focus:border-claroprimaria-01"}`}
                />
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
}
